import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { SurveysTemplate } from "../../../template/surveys/default-template";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { Loader } from "../../../organisms/common/loading";
import { useHistory, useLocation } from "react-router";
import { useAuthContextV2 } from "../../../../auth-state";
import { SurveyR } from "../route";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { RBACHidden } from "../../../helpers/rbac/rbac";
import { useModal } from "../../../../utils/custom-hooks";
import { matchTotalPage } from "../../../helpers/match-total-page";
import { GroupShareSelectField } from "../../../molecules/group-share-select-field";
import {
  connectPageService,
  SurveyStatusTag,
  SurveyListService,
  SurveyListServiceProps,
  CreateSurveyModal,
} from "@graffer-inc/smart-apply";
import { Dropdown } from "../../../molecules/dropdown";
import { SurveyImportFromZipFileModal } from "../modals/survey-import-from-zip-modal";
import { formatDatetime } from "../../../../utils/date-utils";
import {
  InformationBanner,
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { StatusFilterDropDown } from "./status-filter-dropdown";
import { SurveyPublishStatus } from "@graffer-inc/cloud-gov-core";
import { Pagination } from "@graffer-inc/console-design-kit";

const SurveysPage: React.FC<SurveyListServiceProps> = ({
  isLoading,
  surveys,
  paging,
  queries,
  selectedStatus,
  onSearch,
  onCreateNew,
  setPage,
  movePage,
  setQueries,
  fetchSurveys,
  checkSurveyImportZipFile,
  importSurveyFromZipFile,
  setSelectedStatus,
}) => {
  const {
    page: p,
    sort: parsedSort,
    q,
    status,
  } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    sort: string | undefined;
    q: string | undefined;
    status: SurveyPublishStatus | undefined;
  };

  const authState = useAuthContextV2();
  const history = useHistory();
  const createModal = useModal();
  const statusDropdown = useModal();
  const menuToggle = useModal();
  const importSurveyModal = useModal();

  useEffect(() => {
    setPage(parseInt(p!, 10) || 1);
    setQueries({
      sort: (parsedSort as any) || ("desc" as const),
      name: q || "",
      status,
    });
    fetchSurveys();
  }, [p, q, status, setPage, parsedSort, fetchSurveys, setQueries]);

  const defaultGroupId = useMemo(() => {
    if (
      !authState.auth.hasRole("Admin") &&
      authState.auth.groupIDs &&
      authState.auth.groupIDs.length > 0
    ) {
      return authState.auth.groupIDs[0];
    }
    return "";
  }, [authState.auth]);
  const [selectedGroupId, setSelectedGroupId] =
    useState<string>(defaultGroupId);

  return (
    <SurveysTemplate breadcrumbs={[{ to: "#", title: "アンケート一覧" }]}>
      <h1 className="title">
        <i className="fa fa-edit" /> アンケート一覧
      </h1>
      <nav className="level">
        <div className="level-left">
          <div className="level-item">
            <div className="field has-addons">
              <p
                className="control has-icons-left has-icons-right"
                style={{ minWidth: "280px" }}
              >
                <input
                  className="input"
                  placeholder="アンケート名称で検索"
                  value={queries.name}
                  onChange={(e) => setQueries({ name: e.target.value })}
                  style={{
                    fontSize: "14px",
                  }}
                />
                <span className="icon is-small is-left">
                  <i className="fas fa-search" />
                </span>
                <span
                  className="icon is-small is-right"
                  role="button"
                  onClick={() => {
                    setQueries({ name: "" });
                    onSearch();
                  }}
                  style={{ pointerEvents: "initial", cursor: "pointer" }}
                >
                  <i className="fas fa-times" />
                </span>
              </p>
              <p className="control">
                <SecondaryButton text={"検索"} onClick={onSearch} />
              </p>
            </div>
          </div>
        </div>
        <div className="level-right">
          <RBACHidden requirePermissions={"surveys.create"}>
            <p className="level-item">
              <PrimaryButton text={"新規登録"} onClick={createModal.open} />
            </p>
          </RBACHidden>
          {authState.auth.hasPermissions("surveys.create") && (
            <OutsideClickHandler onOutsideClick={menuToggle.close}>
              <Dropdown
                onClick={menuToggle.open}
                isActive={menuToggle.isActive}
                isRight={true}
                menus={[
                  {
                    title: "アンケートをインポートする",
                    onClick: importSurveyModal.open,
                  },
                ]}
              />
            </OutsideClickHandler>
          )}
        </div>
      </nav>
      <Loader isLoading={isLoading}>
        {matchTotalPage(surveys.length, {
          NO_RESOURCE:
            typeof q === "string" && q.length > 0 ? (
              <InformationBanner
                message={"条件に該当するアンケートはありませんでした。"}
                description={"検索文字列を確認してください。"}
              />
            ) : (
              <InformationBanner
                message={"アンケートがまだ登録されていません。"}
                description={"「新規登録」より、アンケートを登録してください。"}
              />
            ),
          RESOURCE_EXISTS: (
            <>
              <table className="table is-fullwidth">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>アンケート名称</th>
                    <th>
                      <StatusFilterDropDown
                        selectedStatus={selectedStatus}
                        onSelect={setSelectedStatus}
                        isActive={statusDropdown.isActive}
                        onOpen={statusDropdown.open}
                        onClose={statusDropdown.close}
                      >
                        公開ステータス
                      </StatusFilterDropDown>
                    </th>
                    <th>受付開始日時</th>
                    <th>受付終了日時</th>
                  </tr>
                </thead>
                <tbody>
                  {surveys.map((a) => (
                    <tr
                      key={a.id}
                      className="c-tr-selectable"
                      onClick={() =>
                        history.push(SurveyR.SurveyResults.link({ id: a.id }))
                      }
                    >
                      <td>{a.id}</td>
                      <td>{a.name}</td>
                      <td>
                        <SurveyStatusTag surveyStatus={a.publishStatus} />
                      </td>
                      <td>
                        {a.publishSetting.startAcceptAt
                          ? formatDatetime(a.publishSetting.startAcceptAt)
                          : "設定なし"}
                      </td>
                      <td>
                        {a.publishSetting.endAcceptAt
                          ? formatDatetime(a.publishSetting.endAcceptAt)
                          : "設定なし"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: "16px" }}>
                <Pagination
                  onPageClick={movePage}
                  currentPage={paging.current}
                  lastPage={paging.total || 1}
                />
              </div>
            </>
          ),
        })}
      </Loader>
      {createModal.isActive && (
        <CreateSurveyModal
          onClose={createModal.close}
          selectedGroupId={selectedGroupId}
          onSave={(name) => {
            onCreateNew(name, selectedGroupId);
            createModal.close();
          }}
        >
          <GroupShareSelectField
            tenantId={authState.auth.data?.orgId!}
            selectedGroupId={selectedGroupId}
            onChange={setSelectedGroupId}
          />
        </CreateSurveyModal>
      )}
      <SurveyImportFromZipFileModal
        isActive={importSurveyModal.isActive}
        isLoading={isLoading}
        groupId={selectedGroupId}
        close={importSurveyModal.close}
        checkImportFile={checkSurveyImportZipFile}
        onSave={async (zipFile, groupId, shallClearCustomPath) => {
          const importedId = await importSurveyFromZipFile(
            zipFile,
            groupId,
            shallClearCustomPath,
          );
          importSurveyModal.close();
          history.push(
            SurveyR.SurveyResults.link({
              id: importedId,
            }),
          );
        }}
      />
    </SurveysTemplate>
  );
};

export const SurveyContainer = connectPageService(
  SurveysPage,
  () => new SurveyListService(),
);
